import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Search,
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Command,
  X,
  FileText,
  Sparkles,
  BookOpen,
  HelpCircle,
  Briefcase,
  ExternalLink,
  Languages,
  Menu,
  Home
} from 'lucide-react';
import toastUtil from '../../utils/toast.js';
import { useSearch } from '../../hooks/useSearch.jsx';
import { localPages } from '../../data/searchData';

export default function TopNav({ onOpenRightSidebar, onOpenMobileSidebar }) {
  const { currentUser, userData, logout } = useAuth();
  const navigate = useNavigate();
  const { language, toggleLanguage, isRTL } = useTheme();
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchRef = useRef(null);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);
  
  // Search functionality - inline dropdown (no API fetching, just local data)
  const {
    searchQuery,
    setSearchQuery,
    results,
    isLoading,
    isSearching,
    hasQuery
  } = useSearch(localPages, null); // Pass null to skip API fetching
  
  const [showSearchResults, setShowSearchResults] = useState(false);

  function getInitials(name) {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  const translations = {
    en: {
      searchPlaceholder: 'Search everything...',
      profile: 'Profile',
      settings: 'Settings',
      logout: 'Logout',
      
    },
    ar: {
      searchPlaceholder: 'ابحث عن أي شيء...',
      profile: 'الملف الشخصي',
      settings: 'الإعدادات',
      logout: 'تسجيل الخروج',
     
    }
  };

  const tr = translations[language === 'ar' ? 'ar' : 'en'];



  const primaryIdentity = userData?.name || currentUser?.displayName || currentUser?.email || '';
  const avatarInitials = primaryIdentity ? getInitials(primaryIdentity) : null;

  useEffect(() => {
    function handleClickOutside(event) {
    
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Keyboard shortcut for search (Cmd/Ctrl + K) - focus on input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('global-search-input');
        if (searchInput) {
          searchInput.focus();
          setShowSearchResults(true);
        }
      }
      if (e.key === 'Escape') {
        setShowSearchResults(false);
        setSearchQuery('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setSearchQuery]);

  const handleLogout = async () => {
    try {
      await logout();
      toastUtil.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toastUtil.error('Failed to logout');
    }
  };

  const handleSearchResultClick = (item) => {
    // Navigate to the selected item
    navigate(item.link);
    setShowSearchResults(false);
    setSearchQuery('');
  };
  
  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSearchResults(true);
  };
  
  const handleSearchInputFocus = () => {
    if (searchQuery.trim()) {
      setShowSearchResults(true);
    }
  };

  return (
    <nav 
      className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB]/50 shadow-sm"
    >
      <div className="px-4 py-3">
        <div className="flex items-center gap-2">
          {/* Mobile: Expandable Search Bar */}
          <AnimatePresence mode="wait">
            {mobileSearchOpen ? (
              <motion.div
                key="mobile-search-input"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '100%', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                className="flex-1 flex items-center gap-2 md:hidden relative"
                ref={mobileSearchRef}
              >
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                  <input
                    id="global-search-input-mobile"
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onFocus={() => setShowSearchResults(true)}
                    autoFocus
                    placeholder={tr.searchPlaceholder}
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[#1E293B] placeholder-[#94A3B8] text-sm font-medium focus:border-[#2563EB] focus:bg-white focus:ring-2 focus:ring-[#2563EB]/10 focus:outline-none transition-all duration-200"
                  />
                  
                  {/* Mobile Search Results Dropdown */}
                  <AnimatePresence>
                    {showSearchResults && (hasQuery || isLoading) && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-[#E2E8F0] shadow-2xl max-h-[60vh] overflow-hidden z-50"
                      >
                        {isLoading ? (
                          <div className="p-6 text-center">
                            <div className="animate-spin w-6 h-6 border-3 border-[#E5E7EB] border-t-[#2F80ED] rounded-full mx-auto mb-2"></div>
                            <p className="text-xs text-[#6B7280]">Loading...</p>
                          </div>
                        ) : results.length > 0 ? (
                          <div className="max-h-[50vh] overflow-y-auto">
                            {results.slice(0, 6).map((result, index) => (
                              <div
                                key={result.id}
                                onClick={() => {
                                  handleSearchResultClick(result);
                                  setMobileSearchOpen(false);
                                }}
                                className="p-3 hover:bg-[#E0F2FF] border-b border-[#E5E7EB] last:border-b-0 cursor-pointer"
                              >
                                <h4 className="text-[#333333] font-semibold text-sm truncate">{result.title}</h4>
                                {result.description && (
                                  <p className="text-[#6B7280] text-xs truncate mt-0.5">{result.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : hasQuery ? (
                          <div className="p-6 text-center">
                            <p className="text-sm text-[#6B7280]">No results found</p>
                          </div>
                        ) : null}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setMobileSearchOpen(false);
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                  className="p-3 rounded-xl bg-[#F8FAFC] hover:bg-red-50 border border-transparent hover:border-red-200 transition-all duration-200 flex-shrink-0"
                >
                  <X className="w-6 h-6 text-[#64748B]" />
                </motion.button>
              </motion.div>
            ) : (
              <motion.div
                key="mobile-icons"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1 md:hidden w-full"
              >
                {/* User Menu */}
                <div className={`relative ${isRTL ? 'mr-auto' : 'ml-auto'}`} ref={userMenuRef}>
                  <motion.button
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    onClick={() => {
                      // Open right sidebar instead of dropdown
                      if (onOpenRightSidebar) {
                        onOpenRightSidebar();
                      } else {
                        setShowUserMenu(!showUserMenu);
                      }
                    }}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#2563EB] hover:shadow-lg transition-all duration-200"
                  >
                    {/* Avatar with online indicator */}
                    <div className="relative">
                      <motion.div 
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E40AF] flex items-center justify-center text-white text-sm font-bold ring-2 ring-white shadow-md"
                        whileHover={{ scale: 1.1 }}
                      >
                        {currentUser?.photoURL ? (
                          <img
                            src={currentUser.photoURL}
                            alt="Profile"
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : avatarInitials ? (
                          avatarInitials
                        ) : (
                          <User className="w-5 h-5 text-white" />
                        )}
                      </motion.div>
                      {/* Online indicator with pulse */}
                      <div className="absolute -bottom-0.5 -right-0.5">
                        <div className="w-3.5 h-3.5 bg-[#10B981] rounded-full ring-2 ring-white"></div>
                        <div className="absolute inset-0 w-3.5 h-3.5 bg-[#10B981] rounded-full animate-ping opacity-75"></div>
                      </div>
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Desktop: Full Search Bar */}
          <div 
            ref={searchRef}
            className="hidden md:block flex-1 max-w-2xl relative"
          >
            <motion.div 
              className="hidden md:block relative group"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <div className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 z-10`}>
                <motion.div
                  animate={isSearching ? { rotate: 360 } : {}}
                  transition={{ duration: 1, repeat: isSearching ? Infinity : 0, ease: "linear" }}
                >
                  <Search className={`w-5 h-5 ${isSearching ? 'text-[#2F80ED]' : 'text-[#9CA3AF]'} transition-colors`} />
                </motion.div>
              </div>
              <input
                id="global-search-input"
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onFocus={handleSearchInputFocus}
                placeholder={tr.searchPlaceholder}
                className={`
                  w-full ${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} py-3.5 
                  bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl
                  text-[#1E293B] placeholder-[#94A3B8] text-sm font-medium
                  focus:border-[#2563EB] focus:bg-white focus:ring-4 focus:ring-[#2563EB]/10 focus:outline-none
                  hover:border-[#CBD5E1] hover:bg-white
                  transition-all duration-200
                `}
              />
              {/* Clear button */}
              {searchQuery && (
                <motion.button
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                  className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/4 -translate-y-1/2 p-1.5 hover:bg-[#F1F5F9] rounded-lg transition-colors z-10`}
                >
                  <X className="w-4 h-4 text-[#64748B]" />
                </motion.button>
              )}
              {/* Keyboard shortcut hint - only show when search is empty */}
              {!searchQuery && (
                <div className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1 z-10`}>
                  <kbd className="px-2 py-1.5 text-[10px] font-bold text-[#64748B] bg-white border border-[#E2E8F0] rounded-md shadow-sm flex items-center gap-1">
                    <Command className="w-3 h-3" />
                    K
                  </kbd>
                </div>
              )}
            </motion.div>
            
            {/* Dropdown Search Results */}
            <AnimatePresence>
              {showSearchResults && (hasQuery || isLoading) && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className={`
                    absolute top-full mt-3
                    left-1/8 -translate-x-1/2 w-[calc(100vw-2rem)]
                    md:left-0 md:right-0 md:translate-x-0 md:w-auto
                    bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl
                    max-h-[500px] overflow-hidden z-50
                  `}
                >
                  {isLoading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2F80ED] rounded-full mx-auto mb-3"></div>
                      <p className="text-sm text-[#6B7280]">Loading search data...</p>
                    </div>
                  ) : results.length > 0 ? (
                    <>
                      <div className="px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E7EB] sticky top-0">
                        <p className="text-xs font-semibold text-[#6B7280]">
                          Found {results.length} result{results.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto professional-scrollbar">
                        {results.slice(0, 10).map((result, index) => (
                          <motion.div
                            key={result.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => handleSearchResultClick(result)}
                            className="group p-4 hover:bg-[#E0F2FF] border-b border-[#E5E7EB] last:border-b-0 cursor-pointer transition-all"
                          >
                            <div className="flex items-start gap-3">
                              {/* Icon based on category */}
                              <div className={`p-2 rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform ${
                                result.category === 'page' ? 'bg-[#E0F2FF] text-[#2F80ED]' :
                                result.category === 'feature' ? 'bg-[#FFD6A5] text-[#F9A826]' :
                                result.category === 'article' ? 'bg-[#B7E4C7] text-[#27AE60]' :
                                'bg-[#E5E7EB] text-[#6B7280]'
                              }`}>
                                {result.category === 'page' && <FileText className="w-4 h-4" />}
                                {result.category === 'feature' && <Sparkles className="w-4 h-4" />}
                                {result.category === 'article' && <BookOpen className="w-4 h-4" />}
                                {result.category === 'help' && <HelpCircle className="w-4 h-4" />}
                                {!['page', 'feature', 'article', 'help'].includes(result.category) && <Briefcase className="w-4 h-4" />}
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-[#333333] font-semibold text-sm mb-1 group-hover:text-[#2F80ED] transition-colors truncate">
                                  {result.title}
                                </h4>
                                {result.description && (
                                  <p className="text-[#6B7280] text-xs line-clamp-1">
                                    {result.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[10px] uppercase font-semibold text-[#9CA3AF] tracking-wider">
                                    {result.category}
                                  </span>
                                  {result.link && (
                                    <>
                                      <span className="text-[10px] text-[#9CA3AF]">•</span>
                                      <span className="text-[10px] text-[#2F80ED] truncate">
                                        {result.link}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Arrow */}
                              <div className="flex-shrink-0 text-[#9CA3AF] group-hover:text-[#2F80ED] group-hover:translate-x-1 transition-all">
                                <ExternalLink className="w-4 h-4" />
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      {results.length > 10 && (
                        <div className="px-4 py-3 bg-[#FAFAFA] border-t border-[#E5E7EB] text-center">
                          <p className="text-xs text-[#6B7280]">
                            Showing top 10 results. Refine your search for more specific results.
                          </p>
                        </div>
                      )}
                    </>
                  ) : hasQuery ? (
                    <div className="p-8 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E5E7EB] rounded-full mb-4">
                        <Search className="w-8 h-8 text-[#6B7280]" />
                      </div>
                      <h3 className="text-base font-bold text-[#333333] mb-2">
                        No Results Found
                      </h3>
                      <p className="text-sm text-[#6B7280]">
                        No matches for "<span className="font-semibold">{searchQuery}</span>". 
                        Try different keywords.
                      </p>
                    </div>
                  ) : null}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Desktop: Right Section - User Menu */}
          <div className={`hidden md:block ${isRTL ? 'mr-auto' : 'ml-auto'}`}>
            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                onClick={() => {
                  // Open right sidebar instead of dropdown
                  if (onOpenRightSidebar) {
                    onOpenRightSidebar();
                  } else {
                    setShowUserMenu(!showUserMenu);
                  }
                }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#2563EB] hover:shadow-lg transition-all duration-200"
              >
                {/* Avatar with online indicator */}
                <div className="relative">
                  <motion.div 
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E40AF] flex items-center justify-center text-white text-sm font-bold ring-2 ring-white shadow-md"
                    whileHover={{ scale: 1.1 }}
                  >
                    {currentUser?.photoURL ? (
                      <img
                        src={currentUser.photoURL}
                        alt="Profile"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : avatarInitials ? (
                      avatarInitials
                    ) : (
                      <User className="w-5 h-5 text-white" />
                    )}
                  </motion.div>
                  {/* Online indicator with pulse */}
                  <div className="absolute -bottom-0.5 -right-0.5">
                    <div className="w-3 h-3 md:w-3.5 md:h-3.5 bg-[#10B981] rounded-full ring-2 ring-white"></div>
                    <div className="absolute inset-0 w-3 h-3 md:w-3.5 md:h-3.5 bg-[#10B981] rounded-full animate-ping opacity-75"></div>
                  </div>
                </div>

                {/* User info */}
                <div className="hidden md:block text-left min-w-0">
                  <p className="text-sm font-bold text-[#1E293B] leading-tight truncate">
                    {(userData?.name || currentUser?.displayName || 'User').split(' ')[0]}
                  </p>
                
                </div>

                {/* Chevron */}
                <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
              </motion.button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-3 w-72 bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] overflow-hidden z-50`}
                  >
                    {/* User Info Header */}
                    <div className="p-5 bg-gradient-to-br from-[#F8FAFC] via-white to-[#EFF6FF] border-b border-[#E2E8F0]">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E40AF] flex items-center justify-center text-white font-bold ring-4 ring-white shadow-lg">
                            {currentUser?.photoURL ? (
                              <img
                                src={currentUser.photoURL}
                                alt="Profile"
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : avatarInitials ? (
                              <span className="text-lg">{avatarInitials}</span>
                            ) : (
                              <User className="w-5 h-5 text-white" />
                            )}
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#10B981] rounded-full ring-2 ring-white"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#1E293B] truncate text-base">
                            {userData?.name || currentUser?.displayName || 'User'}
                          </p>
                          <p className="text-xs text-[#64748B] truncate mt-0.5">
                            {currentUser?.email}
                          </p>
                        </div>
                      </div>
                      {userData?.plan === 'pro' && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white text-xs font-bold rounded-full shadow-md">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          PRO MEMBER
                        </div>
                      )}
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <motion.button
                        whileHover={{ x: 4 }}
                        onClick={() => {
                          navigate('/profile');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-[#1E293B] hover:bg-[#F8FAFC] transition-all group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center group-hover:bg-[#2563EB] transition-colors">
                          <User className="w-4 h-4 text-[#2563EB] group-hover:text-white transition-colors" />
                        </div>
                        <span className="text-sm font-semibold">{tr.profile}</span>
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ x: 4 }}
                        onClick={() => {
                          navigate('/settings');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-[#1E293B] hover:bg-[#F8FAFC] transition-all group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#F1F5F9] flex items-center justify-center group-hover:bg-[#64748B] transition-colors">
                          <Settings className="w-4 h-4 text-[#64748B] group-hover:text-white transition-colors" />
                        </div>
                        <span className="text-sm font-semibold">{tr.settings}</span>
                      </motion.button>
                      
                      <div className="my-2 mx-5 border-t border-[#E2E8F0]" />
                      
                      <motion.button
                        whileHover={{ x: 4 }}
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-5 py-3 text-[#EF4444] hover:bg-red-50 transition-all group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-[#EF4444] transition-colors">
                          <LogOut className="w-4 h-4 text-[#EF4444] group-hover:text-white transition-colors" />
                        </div>
                        <span className="text-sm font-semibold">{tr.logout}</span>
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
